import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { Lock, AlertCircle, Loader } from 'lucide-react';

/**
 * Secure Meeting Join Page
 * 
 * This page:
 * 1. Receives a meeting link with a secure token
 * 2. Verifies the user's credentials
 * 3. Prevents unauthorized access and link sharing
 * 4. Redirects to MiroTalk conference room on success
 */
const SecureMeetingJoin = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();

  const meetingId = searchParams.get('meetingId');
  const joinToken = searchParams.get('token');

  const [step, setStep] = useState('verify'); // verify, loading, joining
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [meetingInfo, setMeetingInfo] = useState(null);

  const fetchMeetingDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/meetings/${meetingId}`);
      setMeetingInfo(response.data);
    } catch (err) {
      // Unauthenticated users will get a 401 here; that's fine — the form still works
      if (err.response?.status !== 401) {
        console.error('Failed to fetch meeting details:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  useEffect(() => {
    // Validate that we have required parameters
    if (!meetingId || !joinToken) {
      setError('Invalid meeting link. Missing required parameters.');
      setStep('error');
      return;
    }

    // Fetch meeting details without joining yet
    fetchMeetingDetails();
  }, [fetchMeetingDetails, joinToken, meetingId]);

  const handleVerifyAndJoin = async (e) => {
    e.preventDefault();

    if (!password.trim()) {
      setError('Please enter your password to verify your identity');
      return;
    }

    if (!user && !email.trim()) {
      setError('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setStep('loading');

      let response;
      if (user) {
        // Already logged in — use the authenticated endpoint
        response = await api.post('/meetings/secure/verify-and-join', {
          meetingId,
          token: joinToken,
          password,
        });
      } else {
        // Not logged in — authenticate + join in a single public request
        response = await api.post('/meetings/secure/token-join', {
          meetingId,
          token: joinToken,
          email: email.trim(),
          password,
        });
      }

      if (response.data.success) {
        console.log('[SecureMeetingJoin] Access granted for:', response.data.userName);
        console.log('[SecureMeetingJoin] Role:', response.data.role);

        if (response.data.embedType === 'YOUTUBE_STREAM' && response.data.streamEmbedUrl) {
          navigate(`/meeting/stream/${meetingId}`, {
            state: {
              streamEmbedUrl: response.data.streamEmbedUrl,
              role: response.data.role,
              meetingTitle: response.data.meetingTitle,
              userName: response.data.userName,
            },
            replace: true,
          });

          return;
        }

        // Redirect to embedded meeting iframe
        // URL is passed via state, NOT in browser address bar, for security
        if (response.data.mirotalkUrl) {
          navigate(`/meeting/embed/${meetingId}`, {
            state: {
              mirotalkUrl: response.data.mirotalkUrl,
              role: response.data.role,
              meetingTitle: response.data.meetingTitle,
              roomId: response.data.roomId,
              userName: response.data.userName,
            },
            replace: true, // Replace history so can't go back to verification page
          });

          return;
        }

        setError('Meeting access could not be prepared. Please try again.');
        setStep('verify');
      } else {
        setError('Failed to generate meeting link. Please try again.');
        setStep('verify');
      }
    } catch (err) {
      console.error('Verification error:', err);
      
      // Better error handling for different failure scenarios
      let errorMsg = 'Verification failed. Please check your credentials.';
      
      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        errorMsg = 'Unable to connect to the server. Check that the backend is running at http://localhost:3010 and try again.';
      } else if (err.response?.status === 401) {
        errorMsg = err.response?.data?.error || 'Invalid credentials. Please check your password.';
      } else if (err.response?.status === 404) {
        errorMsg = 'Meeting not found or invitation link has expired.';
      } else if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      }
      
      setError(errorMsg);
      setStep('verify');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(135deg, ${theme.colors.primary}15 0%, ${theme.colors.secondary}15 100%)`,
      padding: '20px',
    },
    card: {
      backgroundColor: theme.colors.cardBg,
      borderRadius: '12px',
      padding: '40px',
      maxWidth: '500px',
      width: '100%',
      boxShadow: theme.shadows.lg,
      border: `1px solid ${theme.colors.border}`,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '24px',
    },
    headerIcon: {
      width: '40px',
      height: '40px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.primary + '20',
      color: theme.colors.primary,
    },
    title: {
      fontSize: '24px',
      fontWeight: '700',
      color: theme.colors.text,
      margin: 0,
    },
    subtitle: {
      fontSize: '14px',
      color: theme.colors.textSecondary,
      marginBottom: '24px',
    },
    meetingDetails: {
      backgroundColor: theme.colors.background,
      padding: '16px',
      borderRadius: '8px',
      marginBottom: '24px',
      border: `1px solid ${theme.colors.border}`,
    },
    detailLabel: {
      fontSize: '12px',
      color: theme.colors.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '4px',
    },
    detailValue: {
      fontSize: '16px',
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: '12px',
    },
    detailValue_last: {
      marginBottom: 0,
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    securityBadge: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px',
      backgroundColor: theme.colors.success + '15',
      color: theme.colors.success,
      borderRadius: '6px',
      fontSize: '12px',
      marginBottom: '24px',
      fontWeight: '500',
    },
    errorBox: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '12px',
      backgroundColor: theme.colors.danger + '15',
      color: theme.colors.danger,
      borderRadius: '6px',
      fontSize: '13px',
      marginBottom: '16px',
    },
    loadingBox: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
      padding: '40px 20px',
    },
    button: {
      marginTop: '8px',
    },
  };

  if (loading && step === 'loading') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.loadingBox}>
            <Loader size={40} style={{ animation: 'spin 1s linear infinite' }} color={theme.colors.primary} />
            <p style={{ color: theme.colors.textSecondary, margin: 0 }}>
              Preparing your meeting...
            </p>
          </div>
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <div style={styles.headerIcon}>
              <AlertCircle size={24} />
            </div>
            <h1 style={styles.title}>Link Invalid</h1>
          </div>
          <p style={{ ...styles.subtitle, whiteSpace: 'pre-line' }}>{error}</p>
          <Button
            onClick={() => navigate('/user/meetings')}
            style={{ width: '100%' }}
          >
            Back to Meetings
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.headerIcon}>
            <Lock size={24} />
          </div>
          <h1 style={styles.title}>Verify Identity</h1>
        </div>

        <p style={styles.subtitle}>
          Verify your credentials to join the meeting securely.
        </p>

        {meetingInfo && (
          <div style={styles.meetingDetails}>
            <div style={styles.detailLabel}>Meeting</div>
            <div style={styles.detailValue}>{meetingInfo.title}</div>

            {meetingInfo.startTime && (
              <>
                <div style={styles.detailLabel}>Scheduled for</div>
                <div style={styles.detailValue_last}>
                  {new Date(meetingInfo.startTime).toLocaleString()}
                </div>
              </>
            )}
          </div>
        )}

        <div style={styles.securityBadge}>
          <Lock size={14} />
          This link is unique to you. Credential verification prevents unauthorized access.
        </div>

        {error && (
          <div style={styles.errorBox}>
            <AlertCircle size={18} />
            <span style={{ whiteSpace: 'pre-line' }}>{error}</span>
          </div>
        )}

        <form onSubmit={handleVerifyAndJoin} style={styles.form}>
          {!user && (
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: theme.colors.text }}>
                Email
              </label>
              <Input
                type="email"
                placeholder="Enter your account email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
              />
            </div>
          )}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: theme.colors.text }}>
              Password
            </label>
            <Input
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
            />
            <p style={{ fontSize: '12px', color: theme.colors.textSecondary, marginTop: '6px' }}>
              We need your password to verify your identity before granting access.
            </p>
          </div>

          <Button
            type="submit"
            disabled={loading || !password || (!user && !email)}
            style={styles.button}
            fullWidth
          >
            {loading ? 'Verifying...' : 'Verify & Join Meeting'}
          </Button>
        </form>

        <p style={{ fontSize: '12px', color: theme.colors.textSecondary, textAlign: 'center', marginTop: '20px' }}>
          Having trouble? Check that the backend and MiroTalk are running, or <a href="/user/meetings" style={{ color: theme.colors.primary }}>back to meetings</a>
        </p>
      </div>
    </div>
  );
};

export default SecureMeetingJoin;
